/**
 * Demo Pipeline Hook
 *
 * Simulates the 11-stage AISecOps pipeline with staged delays.
 * Produces realistic pipeline state progression for demo mode.
 *
 * Phase S: RETRIEVE stage (pgvector + BM25 → RRF) runs between QUEUE and LLM.
 * For SEARCH_ONLY intent, LLM is skipped — retrieval results go to REVIEW.
 *
 * RECOMMEND stage: Post-pipeline enrichment — generates contextual
 * recommendations after STORAGE completes. Uses mock data keyed by
 * scenario ID. Ratings stored in local state (demo only).
 */

import { useState, useCallback, useRef } from "react";
import type {
  PipelineState,
  PipelineStage,
  StageStatus,
  IntentCategory,
} from "../types/pipeline";
import type { EventLogEntry } from "../types/metrics";
import type { FeedbackRecord } from "../types/api";
import type {
  Recommendation,
  RecommendationRating,
} from "../types/recommendation";
import { PIPELINE_STAGES, ROUTING_DECISIONS } from "../types/pipeline";
import { DEMO_SCENARIOS } from "./scenarios";
import { getMockRecommendations } from "./recommendations";
import { generateEvent } from "./generators";

/**
 * Gap (ms) between one stage completing and the next stage activating.
 * Without this, both transitions fire in the same render cycle — the
 * outgoing stage's green glow, the observability cascade, and the
 * incoming stage's yellow glow all appear simultaneously.
 *
 * 400ms is enough for the eye to register the completed state and see
 * the OTel/Kafka edges start flowing before the next stage lights up.
 */
const INTER_STAGE_GAP = 400;

/** URL regex to detect links anywhere in text */
const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;

/**
 * Mock LLM draft output per scenario (used when REVIEW stage pauses).
 * Note: no entry for "simple-note" — SIMPLE_NOTE skips REVIEW entirely,
 * so this dictionary is never consulted for that scenario. Adding a
 * synthetic LLM draft would reinforce the wrong mental model (R2-1).
 */
const MOCK_DRAFTS: Record<string, { summary: string; tags: string[] }> = {
  "pii-detected": {
    summary:
      "Meeting notes from Q4 budget discussion with [PERSON_1].\n\nPII entities detected and masked: email address, SSN, person name.\nKey topics: quarterly budget review and resource planning.",
    tags: ["meeting-notes", "budget", "pii-masked", "q4"],
  },
  "full-pipeline": {
    summary:
      "Analysis of gRPC vs REST for inter-service communication:\n\n• gRPC: Lower latency (binary protobuf), built-in streaming, strong typing via .proto files. Downsides: harder to debug, limited browser support.\n• REST: Universal tooling, human-readable, easier observability. Downsides: higher latency, no streaming without SSE/WebSocket.\n• Recommendation: gRPC for internal service-to-service, REST for public APIs.",
    tags: [
      "architecture",
      "grpc",
      "rest",
      "microservices",
      "trade-off-analysis",
    ],
  },
  "link-extraction": {
    summary:
      "Extracted from Kubernetes RBAC best practices documentation:\n\n• Apply principle of least privilege for ServiceAccount permissions\n• Use RoleBindings over ClusterRoleBindings where possible\n• Audit RBAC policies regularly with kubectl auth can-i\n• Avoid wildcard permissions in production clusters",
    tags: ["kubernetes", "rbac", "security", "best-practices", "extracted"],
  },
  "audio-transcription": {
    summary:
      "Transcribed audio recording (Whisper STT):\n\nDiscussion about upcoming sprint planning and infrastructure migration timeline. Key action items: finalize k3s cluster config, review Vault integration, schedule load testing.",
    tags: ["audio", "transcription", "sprint-planning", "action-items"],
  },
  "rag-search": {
    summary:
      "Retrieved 3 similar notes via hybrid search (pgvector + BM25 → RRF):\n\n1. [0.92] Kubernetes Pod Security Standards — enforce restricted profile, disable privilege escalation (tags: kubernetes, security, pods)\n2. [0.87] RBAC Configuration Guide — use RoleBindings, audit with can-i, avoid wildcard perms (tags: kubernetes, rbac, security)\n3. [0.74] Network Policy Best Practices — default deny, namespace isolation, egress rules (tags: kubernetes, networking, security)",
    tags: ["search-results", "kubernetes", "security", "rbac", "pods"],
  },
};

export interface DemoPipelineResult {
  pipelineState: PipelineState | null;
  events: EventLogEntry[];
  isRunning: boolean;
  isReviewing: boolean;
  demoReviewData: { summary: string; tags: string[] } | null;
  feedbackHistory: FeedbackRecord[];
  /** Whether the RECOMMEND stage is currently active (generating recs) */
  isRecommending: boolean;
  /** Mock recommendations for the current scenario (null until RECOMMEND completes) */
  demoRecommendations: Recommendation[] | null;
  /** Rate a demo recommendation (local state only, not sent to backend) */
  rateRecommendation: (recId: string, rating: RecommendationRating) => void;
  startPipeline: (input: string, inputType: "TEXT" | "LINK" | "AUDIO") => void;
  startAudioPipeline: (file: Blob, meta: { duration: number }) => void;
  completeReview: (
    action: "accept" | "edit" | "discard",
    editedData?: { summary?: string; tags?: string[] },
  ) => void;
  reset: () => void;
}

function createInitialStages(): Record<PipelineStage, StageStatus> {
  return {
    INPUT: "pending",
    PII_SCAN: "pending",
    GUARDRAILS: "pending",
    CLASSIFY: "pending",
    QUEUE: "pending",
    RETRIEVE: "pending",
    LLM: "pending",
    REVIEW: "pending",
    STORAGE: "pending",
    EMBEDDING: "pending",
    RECOMMEND: "pending",
  };
}

/**
 * Match user input to the best demo scenario based on content analysis.
 */
function matchScenario(input: string): (typeof DEMO_SCENARIOS)[number] {
  const lower = input.toLowerCase();

  // Check for injection patterns
  if (
    (lower.includes("ignore") && lower.includes("instruction")) ||
    lower.includes("system prompt") ||
    lower.includes("jailbreak")
  ) {
    return DEMO_SCENARIOS.find((s) => s.id === "injection-blocked")!;
  }

  // Check for PII patterns
  if (
    lower.includes("@") ||
    /\d{3}-\d{2}-\d{4}/.test(input) ||
    lower.includes("ssn") ||
    lower.includes("phone")
  ) {
    return DEMO_SCENARIOS.find((s) => s.id === "pii-detected")!;
  }

  // Check for URL anywhere in text (not just at start)
  const urlMatches = input.match(URL_REGEX);
  if (urlMatches && urlMatches.length > 0) {
    return DEMO_SCENARIOS.find((s) => s.id === "link-extraction")!;
  }

  // Check for search/retrieval queries (SEARCH_ONLY intent)
  if (
    lower.includes("find") ||
    lower.includes("search") ||
    lower.includes("look up") ||
    lower.includes("similar to") ||
    lower.includes("related notes") ||
    lower.includes("my notes about")
  ) {
    return DEMO_SCENARIOS.find((s) => s.id === "rag-search")!;
  }

  // Check for complex analysis requests
  if (
    lower.includes("analyze") ||
    lower.includes("compare") ||
    lower.includes("trade-off") ||
    lower.includes("evaluate") ||
    input.length > 150
  ) {
    return DEMO_SCENARIOS.find((s) => s.id === "full-pipeline")!;
  }

  // Default to simple note
  return DEMO_SCENARIOS.find((s) => s.id === "simple-note")!;
}

export function useDemoPipeline(): DemoPipelineResult {
  const [pipelineState, setPipelineState] = useState<PipelineState | null>(
    null,
  );
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [isReviewing, setIsReviewing] = useState(false);
  const [demoReviewData, setDemoReviewData] = useState<{
    summary: string;
    tags: string[];
  } | null>(null);
  const [feedbackHistory, setFeedbackHistory] = useState<FeedbackRecord[]>([]);
  const [isRecommending, setIsRecommending] = useState(false);
  const [demoRecommendations, setDemoRecommendations] = useState<
    Recommendation[] | null
  >(null);
  const scenarioRef = useRef<(typeof DEMO_SCENARIOS)[number] | null>(null);
  const noteIdRef = useRef("");

  const addEvent = useCallback((event: EventLogEntry) => {
    setEvents((prev) => [event, ...prev].slice(0, 50));
  }, []);

  const reset = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setPipelineState(null);
    setIsRunning(false);
    setIsReviewing(false);
    setDemoReviewData(null);
    setIsRecommending(false);
    setDemoRecommendations(null);
    scenarioRef.current = null;
    noteIdRef.current = "";
  }, []);

  /**
   * Run a scenario through the simulated pipeline stages.
   */
  const runScenario = useCallback(
    (
      scenario: (typeof DEMO_SCENARIOS)[number],
      noteId: string,
      inputLabel: string,
    ) => {
      const isInjection = scenario.id === "injection-blocked";
      const isSearch = scenario.expectedIntent === "SEARCH_ONLY";
      const isSimpleNote = scenario.expectedIntent === "SIMPLE_NOTE";
      const isAudio = scenario.id === "audio-transcription";
      // SIMPLE_NOTE skips REVIEW (auto-completes in the API), so don't pause
      // for the human-in-the-loop dialog even though the demo loop would
      // otherwise hit the REVIEW stage.
      const needsReview = !isInjection && !isSimpleNote;
      // Stages SIMPLE_NOTE bypasses entirely. STORAGE is intentionally absent —
      // the Note row IS written for SIMPLE_NOTE, just synchronously inside
      // _route_by_intent() rather than from a Celery worker. Mirrors the
      // skipList in `usePipelineState` (see hook for the rationale).
      const SIMPLE_NOTE_SKIP_STAGES: PipelineStage[] = [
        "QUEUE",
        "RETRIEVE",
        "LLM",
        "REVIEW",
        "EMBEDDING",
        "RECOMMEND",
      ];

      // Store refs for completeReview callback
      scenarioRef.current = scenario;
      noteIdRef.current = noteId;

      // Initialize pipeline state
      setPipelineState({
        noteId,
        stages: createInitialStages(),
      });

      addEvent(
        generateEvent(
          "input",
          `New ${inputLabel} input received`,
          "info",
          noteId,
        ),
      );

      let cumulativeDelay = 0;

      // Process each stage sequentially with delays
      PIPELINE_STAGES.forEach((stage, index) => {
        const delay = scenario.stageDelays[stage] || 0;

        // Skip stages with 0 delay (blocked or skipped)
        if (delay === 0 && index > 0) {
          // For injection blocked: mark remaining stages as pending
          if (isInjection && index >= 3) {
            const t = setTimeout(() => {
              setPipelineState((prev) => {
                if (!prev) return prev;
                const newStages = { ...prev.stages };
                newStages[stage] = "pending";
                return { ...prev, stages: newStages };
              });
            }, cumulativeDelay);
            timeoutsRef.current.push(t);
            return;
          }
          // For SEARCH_ONLY: mark LLM as "skipped" (retrieval replaces generation)
          if (isSearch && stage === "LLM") {
            const t = setTimeout(() => {
              setPipelineState((prev) => {
                if (!prev) return prev;
                const newStages = { ...prev.stages };
                newStages[stage] = "skipped";
                return { ...prev, stages: newStages };
              });
              addEvent(
                generateEvent(
                  "llm",
                  "LLM skipped — SEARCH_ONLY intent (retrieval results only)",
                  "info",
                  noteId,
                ),
              );
            }, cumulativeDelay);
            timeoutsRef.current.push(t);
            return;
          }
          // For SIMPLE_NOTE: mark worker-bypassed stages as "skipped". Mirrors
          // the real-pipeline gate in usePipelineState. Without this branch,
          // the demo would either freeze on these stages (delay=0 + no
          // explicit handler) or animate them as "completed" — the exact bug
          // R2-1 fixed.
          if (isSimpleNote && SIMPLE_NOTE_SKIP_STAGES.includes(stage)) {
            const t = setTimeout(() => {
              setPipelineState((prev) => {
                if (!prev) return prev;
                const newStages = { ...prev.stages };
                newStages[stage] = "skipped";
                return { ...prev, stages: newStages };
              });
              // Emit one combined event when the first skipped stage fires
              // (QUEUE), so demo viewers see explicit confirmation that the
              // skip was intentional. Per-stage events would spam the log
              // with six near-identical lines; one combined message keeps
              // the event panel readable while still narrating the bypass.
              if (stage === SIMPLE_NOTE_SKIP_STAGES[0]) {
                addEvent(
                  generateEvent(
                    "classify",
                    "QUEUE/RETRIEVE/LLM/REVIEW/EMBEDDING/RECOMMEND skipped — SIMPLE_NOTE direct-save (service.py:379)",
                    "info",
                    noteId,
                  ),
                );
              }
            }, cumulativeDelay);
            timeoutsRef.current.push(t);
            return;
          }
        }

        // Pause at REVIEW for user confirmation (Draft→Confirm→Commit flow)
        if (needsReview && stage === "REVIEW") {
          const reviewActiveTimeout = setTimeout(() => {
            setPipelineState((prev) => {
              if (!prev) return prev;
              const newStages = { ...prev.stages };
              newStages.REVIEW = "active";
              return { ...prev, stages: newStages };
            });
            setIsReviewing(true);
            setDemoReviewData(
              MOCK_DRAFTS[scenario.id] || MOCK_DRAFTS["full-pipeline"],
            );
            addEvent(
              generateEvent(
                "review",
                "Draft ready — awaiting user confirmation",
                "warning",
                noteId,
              ),
            );
          }, cumulativeDelay);
          timeoutsRef.current.push(reviewActiveTimeout);
          return;
        }

        // Skip STORAGE, EMBEDDING, and RECOMMEND scheduling — completeReview handles all three
        // (EMBEDDING chains from STORAGE, RECOMMEND chains from EMBEDDING)
        if (
          needsReview &&
          (stage === "STORAGE" ||
            stage === "EMBEDDING" ||
            stage === "RECOMMEND")
        ) {
          return;
        }

        // Set stage to active
        const activeTimeout = setTimeout(() => {
          setPipelineState((prev) => {
            if (!prev) return prev;
            const newStages = { ...prev.stages };
            newStages[stage] = "active";
            return { ...prev, stages: newStages };
          });

          // Generate stage-specific events
          const stageEvents: Record<string, () => void> = {
            INPUT: () => {
              if (isAudio) {
                addEvent(
                  generateEvent(
                    "input",
                    "Whisper STT processing audio...",
                    "info",
                    noteId,
                  ),
                );
              }
            },
            PII_SCAN: () => {
              if (scenario.id === "pii-detected") {
                addEvent(
                  generateEvent(
                    "pii",
                    "3 PII entities detected: email, SSN, person name",
                    "warning",
                    noteId,
                  ),
                );
              } else {
                addEvent(
                  generateEvent(
                    "pii",
                    "No PII detected in content",
                    "success",
                    noteId,
                  ),
                );
              }
            },
            GUARDRAILS: () => {
              if (isInjection) {
                addEvent(
                  generateEvent(
                    "guardrail",
                    "BLOCKED: Prompt injection attempt detected",
                    "error",
                    noteId,
                  ),
                );
              } else {
                addEvent(
                  generateEvent(
                    "guardrail",
                    "Content passed all guardrail checks",
                    "success",
                    noteId,
                  ),
                );
              }
            },
            CLASSIFY: () => {
              addEvent(
                generateEvent(
                  "classify",
                  `Intent: ${scenario.expectedIntent} (confidence: 0.${Math.floor(85 + Math.random() * 10)})`,
                  "info",
                  noteId,
                ),
              );
            },
            RETRIEVE: () => {
              const isSearch = scenario.expectedIntent === "SEARCH_ONLY";
              if (isSearch) {
                addEvent(
                  generateEvent(
                    "retrieve",
                    "RAG hybrid search: pgvector (3 dense) + BM25 (5 sparse) → RRF fusion → 3 results",
                    "info",
                    noteId,
                  ),
                );
              } else {
                addEvent(
                  generateEvent(
                    "retrieve",
                    "RAG context: 2 similar notes retrieved for LLM augmentation",
                    "info",
                    noteId,
                  ),
                );
              }
            },
            LLM: () => {
              addEvent(
                generateEvent(
                  "llm",
                  "LLM processing started (Llama 3.1 8B)",
                  "info",
                  noteId,
                ),
              );
            },
          };

          stageEvents[stage]?.();
        }, cumulativeDelay);
        timeoutsRef.current.push(activeTimeout);

        cumulativeDelay += delay;

        // Set stage to completed (or failed for injection at GUARDRAILS)
        const completeTimeout = setTimeout(() => {
          setPipelineState((prev) => {
            if (!prev) return prev;
            const newStages = { ...prev.stages };

            if (isInjection && stage === "GUARDRAILS") {
              newStages[stage] = "failed";
              // Mark all subsequent stages as pending
              PIPELINE_STAGES.slice(index + 1).forEach((s) => {
                newStages[s] = "pending";
              });
              return { ...prev, stages: newStages };
            }

            newStages[stage] = "completed";

            // Add classify data when CLASSIFY completes
            if (stage === "CLASSIFY") {
              const intentMap: Record<string, IntentCategory> = {
                "simple-note": "SIMPLE_NOTE",
                "pii-detected": "SUMMARIZE",
                "full-pipeline": "REASON",
                "rag-search": "SEARCH_ONLY",
                "link-extraction": "EXTRACT",
                "audio-transcription": "SUMMARIZE",
              };
              const intent: IntentCategory =
                intentMap[scenario.id] || "SUMMARIZE";
              return {
                ...prev,
                stages: newStages,
                classify: {
                  intent,
                  confidence: 0.85 + Math.random() * 0.12,
                  // Pull from ROUTING_DECISIONS so demo and real pipeline stay
                  // in sync. Hardcoding the strings here previously caused the
                  // demo to keep showing "Lightweight LLM Processing" for
                  // SIMPLE_NOTE even after the real label was corrected.
                  routingDecision: ROUTING_DECISIONS[intent],
                },
              };
            }

            return { ...prev, stages: newStages };
          });

          // Final stage: stop running
          if (stage === "STORAGE" || (isInjection && stage === "GUARDRAILS")) {
            setIsRunning(false);

            if (isInjection) {
              addEvent(
                generateEvent(
                  "error",
                  "Pipeline TERMINATED — security threat blocked",
                  "error",
                  noteId,
                ),
              );
            } else {
              addEvent(
                generateEvent(
                  "storage",
                  "Note processed and stored successfully",
                  "success",
                  noteId,
                ),
              );
            }
          }
        }, cumulativeDelay);
        timeoutsRef.current.push(completeTimeout);

        // Push next stage's activation forward so the completed glow and
        // observability cascade are visible before the next stage lights up.
        cumulativeDelay += INTER_STAGE_GAP;
      });
    },
    [addEvent],
  );

  /**
   * Complete the REVIEW stage after user confirmation.
   * Handles Accept (store), Edit (store with edits), and Discard (skip storage).
   */
  const completeReview = useCallback(
    (
      action: "accept" | "edit" | "discard",
      editedData?: { summary?: string; tags?: string[] },
    ) => {
      setIsReviewing(false);
      const noteId = noteIdRef.current;
      const originalData = demoReviewData;

      setDemoReviewData(null);

      // Build feedback record (RLHF signal)
      if (originalData) {
        const diffFields: string[] = [];
        if (action === "edit" && editedData) {
          if (
            editedData.summary &&
            editedData.summary !== originalData.summary
          ) {
            diffFields.push("summary");
          }
          if (
            editedData.tags &&
            JSON.stringify(editedData.tags) !==
              JSON.stringify(originalData.tags)
          ) {
            diffFields.push("tags");
          }
        }

        const record: FeedbackRecord = {
          userId: "demo-user",
          noteId,
          timestamp: new Date().toISOString(),
          action,
          originalSummary: originalData.summary,
          originalTags: originalData.tags,
          ...(action === "edit" && editedData
            ? {
                editedSummary: editedData.summary,
                editedTags: editedData.tags,
                diffFields,
              }
            : {}),
        };

        setFeedbackHistory((prev) => [...prev, record]);

        addEvent(
          generateEvent(
            "feedback",
            `Feedback recorded — ${action === "accept" ? "positive signal" : action === "edit" ? `correction signal (${diffFields.join(", ")} changed)` : "negative signal"} captured for model improvement`,
            "info",
            noteId,
          ),
        );
      }

      // Mark REVIEW as completed
      setPipelineState((prev) => {
        if (!prev) return prev;
        const newStages = { ...prev.stages };
        newStages.REVIEW = "completed";
        return { ...prev, stages: newStages };
      });

      if (action === "discard") {
        // Discard → no storage → no embedding → no recommendations (all skipped)
        setPipelineState((prev) => {
          if (!prev) return prev;
          const newStages = { ...prev.stages };
          newStages.EMBEDDING = "skipped";
          newStages.RECOMMEND = "skipped";
          return { ...prev, stages: newStages };
        });
        setIsRunning(false);
        addEvent(
          generateEvent("review", "Draft discarded by user", "warning", noteId),
        );
        addEvent(
          generateEvent(
            "embedding",
            "EMBEDDING skipped — discarded note not stored",
            "info",
            noteId,
          ),
        );
        addEvent(
          generateEvent(
            "recommend",
            "RECOMMEND skipped — no embedding to search",
            "info",
            noteId,
          ),
        );
        return;
      }

      // Accept or Edit → commit to STORAGE
      addEvent(
        generateEvent(
          "review",
          action === "accept"
            ? "Draft accepted — committing to storage"
            : "Draft edited and accepted — committing to storage",
          "success",
          noteId,
        ),
      );

      const storageDelay = scenarioRef.current?.stageDelays.STORAGE || 400;

      // STORAGE: active (after gap for REVIEW's obs cascade to start flowing)
      const activeT = setTimeout(() => {
        setPipelineState((prev) => {
          if (!prev) return prev;
          const newStages = { ...prev.stages };
          newStages.STORAGE = "active";
          return { ...prev, stages: newStages };
        });
      }, INTER_STAGE_GAP);
      timeoutsRef.current.push(activeT);

      // STORAGE: completed → chain EMBEDDING
      const storageCompleteTime = INTER_STAGE_GAP + storageDelay;
      const completeT = setTimeout(() => {
        setPipelineState((prev) => {
          if (!prev) return prev;
          const newStages = { ...prev.stages };
          newStages.STORAGE = "completed";
          return { ...prev, stages: newStages };
        });
        addEvent(
          generateEvent(
            "storage",
            "Note processed and stored successfully",
            "success",
            noteId,
          ),
        );
      }, storageCompleteTime);
      timeoutsRef.current.push(completeT);

      // EMBEDDING: active (chained from STORAGE completion)
      const embeddingDelay = scenarioRef.current?.stageDelays.EMBEDDING || 1500;
      const embActiveT = setTimeout(() => {
        setPipelineState((prev) => {
          if (!prev) return prev;
          const newStages = { ...prev.stages };
          newStages.EMBEDDING = "active";
          return { ...prev, stages: newStages };
        });
        addEvent(
          generateEvent(
            "embedding",
            "Generating 768-dim vector embedding (pgvector)...",
            "info",
            noteId,
          ),
        );
      }, storageCompleteTime + INTER_STAGE_GAP);
      timeoutsRef.current.push(embActiveT);

      // EMBEDDING: completed → chain RECOMMEND
      const embeddingCompleteTime =
        storageCompleteTime + INTER_STAGE_GAP + embeddingDelay;
      const embCompleteT = setTimeout(() => {
        setPipelineState((prev) => {
          if (!prev) return prev;
          const newStages = { ...prev.stages };
          newStages.EMBEDDING = "completed";
          return { ...prev, stages: newStages };
        });
        addEvent(
          generateEvent(
            "embedding",
            "768-dim vector stored in pgvector (HNSW index)",
            "success",
            noteId,
          ),
        );
      }, embeddingCompleteTime);
      timeoutsRef.current.push(embCompleteT);

      // RECOMMEND: active (chained from EMBEDDING completion — needs vector for RAG)
      const recommendDelay = scenarioRef.current?.stageDelays.RECOMMEND || 2500;
      const recActiveT = setTimeout(() => {
        setPipelineState((prev) => {
          if (!prev) return prev;
          const newStages = { ...prev.stages };
          newStages.RECOMMEND = "active";
          return { ...prev, stages: newStages };
        });
        setIsRecommending(true);
        addEvent(
          generateEvent(
            "recommend",
            "Generating contextual recommendations (RAG + LLM)...",
            "info",
            noteId,
          ),
        );
      }, embeddingCompleteTime + INTER_STAGE_GAP);
      timeoutsRef.current.push(recActiveT);

      // RECOMMEND: completed with mock data
      const recCompleteT = setTimeout(
        () => {
          const scenarioId = scenarioRef.current?.id || "simple-note";
          const mockRecs = getMockRecommendations(scenarioId);

          setPipelineState((prev) => {
            if (!prev) return prev;
            const newStages = { ...prev.stages };
            newStages.RECOMMEND = "completed";
            return { ...prev, stages: newStages };
          });
          setIsRecommending(false);
          setDemoRecommendations(mockRecs);
          setIsRunning(false);

          const recCount = mockRecs?.length ?? 0;
          addEvent(
            generateEvent(
              "recommend",
              `${recCount} recommendation${recCount !== 1 ? "s" : ""} generated for note`,
              "success",
              noteId,
            ),
          );
        },
        embeddingCompleteTime + INTER_STAGE_GAP + recommendDelay,
      );
      timeoutsRef.current.push(recCompleteT);
    },
    [addEvent, demoReviewData],
  );

  const startPipeline = useCallback(
    (input: string, inputType: "TEXT" | "LINK" | "AUDIO") => {
      reset();
      setIsRunning(true);

      const scenario = matchScenario(input);
      const noteId = `demo-${Date.now().toString(36)}`;

      runScenario(scenario, noteId, inputType.toLowerCase());
    },
    [reset, runScenario],
  );

  const startAudioPipeline = useCallback(
    (_file: Blob, meta: { duration: number }) => {
      reset();
      setIsRunning(true);

      const scenario = DEMO_SCENARIOS.find(
        (s) => s.id === "audio-transcription",
      )!;
      const noteId = `demo-${Date.now().toString(36)}`;

      // Add extra audio-specific event before running the scenario
      addEvent(
        generateEvent(
          "input",
          `Audio recording received (${meta.duration}s)`,
          "info",
          noteId,
        ),
      );

      runScenario(scenario, noteId, "audio");
    },
    [reset, addEvent, runScenario],
  );

  /**
   * Rate a demo recommendation (local state only).
   * Updates the recommendation in the local array and emits a demo event.
   */
  const rateRecommendation = useCallback(
    (recId: string, rating: RecommendationRating) => {
      setDemoRecommendations((prev) => {
        if (!prev) return prev;
        return prev.map((rec) =>
          rec.id === recId
            ? { ...rec, rating, ratedAt: new Date().toISOString() }
            : rec,
        );
      });
      addEvent(
        generateEvent(
          "recommend",
          `Recommendation rated ${rating}/3 — ${rating === 3 ? "excellent (flywheel candidate)" : rating === 0 ? "irrelevant" : `score ${rating}`}`,
          "info",
          noteIdRef.current,
        ),
      );
    },
    [addEvent],
  );

  return {
    pipelineState,
    events,
    isRunning,
    isReviewing,
    demoReviewData,
    feedbackHistory,
    isRecommending,
    demoRecommendations,
    rateRecommendation,
    startPipeline,
    startAudioPipeline,
    completeReview,
    reset,
  };
}
